import { useMemo } from 'react';
import { Plug } from 'lucide-react';
import type {
  TableConnector,
  NangoConnectorConfig,
  HubspotDirectConnectorConfig,
  ConnectorType,
  IntegrationConnection,
} from '@loader/shared-types';

import { ConnectionGroup } from './ConnectionGroup';
import { ConnectorTypeGroup } from './ConnectorTypeGroup';
import { useNangoConnectionAdapter, useNativeConnectionAdapter } from '../hooks/useConnectionAdapters';
import { CONNECTOR_META, NATIVE_PROVIDERS } from '../types';
import type { WorkspaceNangoConnection } from '../hooks/useNangoConnectors';

import type { Table } from '@/lib/types';
import { EmptyView } from '@/components/common';

// ============================================================================
// Group data types
// ============================================================================

interface BaseConnectionGroupData {
  connectionId: string;
  displayName: string;
  connectors: TableConnector[];
  tables: Table[];
}

interface NangoConnectionGroupData extends BaseConnectionGroupData {
  connectionType: 'nango';
  providerConfigKey: string;
}

interface NativeConnectionGroupData extends BaseConnectionGroupData {
  connectionType: 'native';
  provider: string;
}

type ConnectionGroupData = NangoConnectionGroupData | NativeConnectionGroupData;

interface TypeGroupData {
  type: ConnectorType;
  connectors: TableConnector[];
  tables: Table[];
}

// ============================================================================
// Props
// ============================================================================

interface IntegrationsListProps {
  tables: Table[];
  connectors: TableConnector[];
  nangoConnections?: WorkspaceNangoConnection[];
  integrationConnections?: IntegrationConnection[];
  loading: boolean;
  error?: Error | null;
  /** Nango: activate a sync (create table from connection) */
  onNangoActivate: (connectionId: string, providerConfigKey: string, syncKey: string) => void;
  /** Nango: disconnect a connection */
  onNangoDisconnect: (connectionId: string, providerConfigKey: string) => void;
  /** Native: activate a sync (create table for an object type) */
  onNativeActivate: (connectionId: string, syncKey: string) => void;
  /** Native: disconnect a connection */
  onNativeDisconnect: (connectionId: string) => void;
  /** Native: re-authenticate to refresh tokens/scopes */
  onNativeReconnect?: (provider: string) => void;
}

// ============================================================================
// Connector type set for native providers (computed once)
// ============================================================================

const NATIVE_CONNECTOR_TYPES = new Set(
  Object.values(NATIVE_PROVIDERS).map((p) => p.connectorType),
);

// ============================================================================
// Component
// ============================================================================

export function IntegrationsList({
  tables,
  connectors,
  nangoConnections,
  integrationConnections,
  loading,
  error,
  onNangoActivate,
  onNangoDisconnect,
  onNativeActivate,
  onNativeDisconnect,
  onNativeReconnect,
}: IntegrationsListProps) {
  const tableMap = useMemo(() => {
    const map = new Map<string, Table>();
    for (const t of tables) map.set(t.id, t);
    return map;
  }, [tables]);

  const { connectionGroups, typeGroups } = useMemo(() => {
    const connections = new Map<string, ConnectionGroupData>();
    const byType = new Map<ConnectorType, TypeGroupData>();

    // Seed Nango groups from workspace connections
    for (const nc of nangoConnections ?? []) {
      if (!connections.has(nc.connectionId)) {
        connections.set(nc.connectionId, {
          connectionId: nc.connectionId,
          connectionType: 'nango',
          displayName: nc.providerConfigKey,
          connectors: [],
          tables: [],
          providerConfigKey: nc.providerConfigKey,
        });
      }
    }

    // Seed native groups from integration connections
    for (const ic of integrationConnections ?? []) {
      if (ic.provider in NATIVE_PROVIDERS) {
        connections.set(ic.id, {
          connectionId: ic.id,
          connectionType: 'native',
          provider: ic.provider,
          displayName: ic.display_name,
          connectors: [],
          tables: [],
        });
      }
    }

    // Distribute connectors into groups
    for (const conn of connectors) {
      const table = tableMap.get(conn.table_id);

      if (conn.type === 'nango') {
        const config = conn.config as NangoConnectorConfig;
        const key = config.nango_connection_id;
        if (!key) continue;

        if (!connections.has(key)) {
          connections.set(key, {
            connectionId: key,
            connectionType: 'nango',
            displayName: config.nango_provider_config_key,
            connectors: [],
            tables: [],
            providerConfigKey: config.nango_provider_config_key,
          });
        }

        const group = connections.get(key)!;
        group.connectors.push(conn);
        if (table) group.tables.push(table);
      } else if (NATIVE_CONNECTOR_TYPES.has(conn.type)) {
        const config = conn.config as HubspotDirectConnectorConfig;
        const connId = config.connection_id;
        if (connId && connections.has(connId)) {
          const group = connections.get(connId)!;
          group.connectors.push(conn);
          if (table) group.tables.push(table);
        }
      } else {
        if (!byType.has(conn.type)) {
          byType.set(conn.type, { type: conn.type, connectors: [], tables: [] });
        }
        const group = byType.get(conn.type)!;
        group.connectors.push(conn);
        if (table) group.tables.push(table);
      }
    }

    return {
      connectionGroups: Array.from(connections.values()).sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      ),
      typeGroups: Array.from(byType.values()).sort((a, b) => {
        const aLabel = CONNECTOR_META[a.type]?.label ?? a.type;
        const bLabel = CONNECTOR_META[b.type]?.label ?? b.type;
        return aLabel.localeCompare(bLabel);
      }),
    };
  }, [connectors, tableMap, nangoConnections, integrationConnections]);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="bg-muted h-16 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-muted-foreground text-sm">Failed to load integrations. Please try again.</p>
      </div>
    );
  }

  const hasContent = connectionGroups.length > 0 || typeGroups.length > 0;

  if (!hasContent) {
    return (
      <EmptyView
        title="No integrations yet"
        description="Connect an external data source to automatically create and sync tables."
        icon={Plug}
      />
    );
  }

  return (
    <div className="space-y-3">
      {connectionGroups.map((group) =>
        group.connectionType === 'nango' ? (
          <NangoConnectionGroupWrapper
            key={group.connectionId}
            group={group}
            onActivate={onNangoActivate}
            onDisconnect={onNangoDisconnect}
          />
        ) : (
          <NativeConnectionGroupWrapper
            key={group.connectionId}
            group={group}
            onActivate={onNativeActivate}
            onDisconnect={onNativeDisconnect}
            onReconnect={onNativeReconnect}
          />
        ),
      )}

      {typeGroups.map((group) => (
        <ConnectorTypeGroup
          key={group.type}
          type={group.type}
          connectors={group.connectors}
          tables={group.tables}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Thin wrapper components (must be separate to call provider-specific hooks)
// ============================================================================

function NangoConnectionGroupWrapper({
  group,
  onActivate,
  onDisconnect,
}: {
  group: NangoConnectionGroupData;
  onActivate: (connectionId: string, providerConfigKey: string, syncKey: string) => void;
  onDisconnect: (connectionId: string, providerConfigKey: string) => void;
}) {
  const adapter = useNangoConnectionAdapter(group.connectionId, group.providerConfigKey);

  return (
    <ConnectionGroup
      {...adapter}
      connectors={group.connectors}
      tables={group.tables}
      onActivate={(syncKey) => onActivate(group.connectionId, group.providerConfigKey, syncKey)}
      onDisconnect={() => onDisconnect(group.connectionId, group.providerConfigKey)}
    />
  );
}

function NativeConnectionGroupWrapper({
  group,
  onActivate,
  onDisconnect,
  onReconnect,
}: {
  group: NativeConnectionGroupData;
  onActivate: (connectionId: string, syncKey: string) => void;
  onDisconnect: (connectionId: string) => void;
  onReconnect?: (provider: string) => void;
}) {
  const adapter = useNativeConnectionAdapter(group.provider);

  return (
    <ConnectionGroup
      {...adapter}
      label={group.displayName}
      connectors={group.connectors}
      tables={group.tables}
      onActivate={(syncKey) => onActivate(group.connectionId, syncKey)}
      onDisconnect={() => onDisconnect(group.connectionId)}
      onReconnect={onReconnect ? () => onReconnect(group.provider) : undefined}
    />
  );
}
